/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CourseHeader
 *
 * @file CourseHeader.jsx
 * @author Gladson Samuel S
 * @since 1.0.0
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, TextField, Typography, useMediaQuery } from '@material-ui/core';
import { BUTTON_TYPES } from '../../common/constants';
import CustomCard from '../common/CustomCard';
import Icon from '../icons/Icon';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import Download from '../download/Download';

const EtextCard = (props) => {
  const isScreenBelow768 = !useMediaQuery('@media screen and (min-width: 768px)');

  const {
    book = {},
    classes = {},
    config = {}
  } = props;

  const {
    showGoToPage = true,
    showBookActions = true,
    showBookTitle = true,
    showAuthor = true,
    showBookCover = true,
    showSccessEducatorGuides = true,
    showTitleInfo = true,
    showDownload = true
  } = config;

  const renderTitleInfo = () => {
    if (!showTitleInfo) {
      return null;
    }

    const { tooltip, callback, showHotspot = false } = book.titleInfo || {};

    return (
      <TooltipWrapper tooltip={tooltip || ''} placement="top">
        <IconButton
          className={classes.iconButton}
          onClick={() => {
            callback?.();
          }}
          aria-haspopup="dialog"
        >
          {showHotspot && <div className={classes.hotspot} />}
          <Icon name="InfoIcon" className={classes.infoIcon} />
        </IconButton>
      </TooltipWrapper>
    );
  };

  const renderTitle = () => {
    if (!showBookTitle || !book.title) {
      return null;
    }

    return (
      <div className={classes.titleInfoContainer}>
        <Typography className={classes.bookTitle}>
          {book.title}
        </Typography>
        {renderTitleInfo()}
      </div>
    );
  };

  const renderBookCover = () => {
    if (!showBookCover || !book.coverImage) {
      return null;
    }

    const { progress, status, onCancelClick, onBookClick } = book?.titleInfo || {};

    return (
      <div className={classes.coverImageContainer}>
        {showDownload ?
          <Download
            backgroundImage={book?.coverImage}
            progress={progress}
            downloadStatus={status}
            onCancelClick={onCancelClick}
            onClick={onBookClick}
            classes={{
              root: classes.download
            }}
          />
          : <img src={book.coverImage} alt="Book cover" className={classes.coverImage} />
        }
      </div>
    );
  };

  const renderAuthor = () => {
    if (!showAuthor || !book.author) {
      return null;
    }

    return (
      <Typography className={classes.author}>
        {book.author}
      </Typography>
    );
  };

  const renderTextButton = (action) => {
    if (!action.text) {
      return null;
    }

    return (
      <Button
        classes={{
          label: classes.secondaryButtonText,
          root: classes.secondaryButton,
          focusVisible: classes.focusVisible
        }}
        onClick={action.callback}
      >
        {action.text}
      </Button>
    );
  };

  /**
   * Rendes Go to page
   *
   */
  const renderGoToPage = () => {
    if (!showGoToPage || !book.goToPage) return null;

    const {
      label,
      placeholder,
      value,
      maxLength,
      callback
    } = book.goToPage || {};

    return (
      <div className={classes.goToPageContainer}>
        {label && <Typography className={classes.goToPageLabel}>{label}</Typography>}
        <TextField
          className={classes.goToTextField}
          value={value}
          placeholder={placeholder}
          onChange={(event) => callback(event?.target?.value)}
          inputProps={{
            maxLength
          }}
        />
      </div>
    );
  };

  const renderAction = (action) => {
    if (!action.callback) {
      return null;
    }

    if (action.type === BUTTON_TYPES.BUTTON) {
      return renderTextButton(action);
    }

    return null;
  };

  const renderActions = () => {
    if (!showBookActions || !book.actions) {
      return null;
    }

    return (
      <div className={classes.actionsContainer}>
        {book.actions.map((action) => renderAction(action))}
      </div>
    );
  };

  const renderAccessEducatorGuides = () => {
    if (!showSccessEducatorGuides || !book.accessEducatorGuides) return null;

    const { text, callback } = book.accessEducatorGuides || {};

    return (
      <Button
        classes={{
          label: classes.secondaryGuidesButtonText,
          root: classes.secondaryGuidesButton,
          focusVisible: classes.focusVisible
        }}
        onClick={callback}
      >
        {text}
      </Button>
    );
  };

  return (
    <Fragment>
      <CustomCard
        className={`${classes.card} ${classes.book}`}
      >
        {isScreenBelow768 ? null : renderBookCover()}
        <div className={classes.bookInfo}>
          {renderTitle()}
          {renderAuthor()}
          <div className={classes.bookActionsContainer}>
            {isScreenBelow768 ? renderBookCover() : null}
            <div className={classes.bookActions}>
              {renderGoToPage()}
              {renderActions()}
              {renderAccessEducatorGuides()}
            </div>
          </div>
        </div>
      </CustomCard>
    </Fragment>
  );
};

EtextCard.propTypes = {
  book: PropTypes.object,
  classes: PropTypes.object,
  config: PropTypes.object
};

EtextCard.defaultProps = {
  book: {},
  classes: {},
  config: {}
};

export default EtextCard;
